// API配置参数（多租户动态加载版）
// 不再写死 companyId、apiToken 等配置，改为从 Node.js 中间层动态拉取

import clientConfig from './client_config.js'
import { getOrigin } from '../utils/auth_access_util.js'

let appConfig = null

/**
 * 从 Node.js 中间层加载租户配置
 * 必须在 App 初始化阶段调用
 */
export async function loadAppConfig() {
    try {
        // 优先从 localStorage 取 tenantKey/companyId 作为 query 参数（绕过 session 依赖）
        // 两个参数都传：后端 getByCompanyId/getByTenantKey 按需兜底（新企业 companyId 可能缺失）
        const cachedTenantKey = localStorage.getItem('tenantKey') || ''
        const cachedCompanyId = localStorage.getItem('companyId') || (appConfig && appConfig.companyId) || ''
        const q = []
        if (cachedTenantKey) q.push(`tenantKey=${encodeURIComponent(cachedTenantKey)}`)
        if (cachedCompanyId) q.push(`companyId=${encodeURIComponent(cachedCompanyId)}`)
        q.push(`_t=${Date.now()}`)
        const url = `${getOrigin(clientConfig.apiPort)}${clientConfig.getTenantConfigPath}?${q.join('&')}`
        const res = await fetch(url, {
            credentials: 'include'
        })
        const json = await res.json()
        if (json.code === 0 && json.data) {
            appConfig = json.data
            // 存配置主键为 tenantId（申请单/预约列表按 tenantId 精确过滤，多企业共享 companyId 防串数据）
            if (appConfig.id) {
                localStorage.setItem('tenantId', appConfig.id)
            }
            console.log('[多租户] 租户配置加载成功:', appConfig)
            return true
        }
        console.error('[多租户] 租户配置加载失败:', json.msg || json.message || '未知错误')
        return false
    } catch (e) {
        console.error('[多租户] 租户配置加载异常:', e)
        return false
    }
}

/**
 * 免登成功后重新拉取租户配置（此时 Session 已有 tenantConfig）
 * 只使用真实后端数据，不降级到默认配置
 * @param {string} [tenantKey] - 从免登响应中拿到的 tenant_key，传参优先于 localStorage
 */
export async function refreshAppConfig(tenantKey) {
    try {
        // 优先用免登返回的 tenantKey，其次从 localStorage 取
        const key = tenantKey || localStorage.getItem('tenantKey') || ''
        // 同时携带 companyId，便于后端在 tenantKey 缺失时按企业ID兜底查询
        const companyId = (appConfig && appConfig.companyId) || localStorage.getItem('companyId') || ''
        const query = []
        if (key) query.push(`tenantKey=${encodeURIComponent(key)}`)
        if (companyId) query.push(`companyId=${encodeURIComponent(companyId)}`)
        query.push(`_t=${Date.now()}`)
        const url = `${getOrigin(clientConfig.apiPort)}${clientConfig.getTenantConfigPath}?${query.join('&')}`
        const res = await fetch(url, {
            credentials: 'include'
        })
        const json = await res.json()
        if (json.code === 0 && json.data) {
            appConfig = json.data
            console.log('[多租户] 免登后租户配置刷新成功:', appConfig)
            // 缓存 tenantKey 供后续 fallback 使用
            if (appConfig.tenantKey) {
                localStorage.setItem('tenantKey', appConfig.tenantKey)
            }
            // 缓存配置主键为 tenantId（申请单/预约列表按 tenantId 精确过滤）
            if (appConfig.id) {
                localStorage.setItem('tenantId', appConfig.id)
            }
            return true
        }
        console.warn('[多租户] 免登后刷新配置失败，保留现有配置:', json.msg || json.message)
        return false
    } catch (e) {
        console.warn('[多租户] 免登后刷新配置异常，保留现有配置:', e)
        return false
    }
}

/**
 * 获取当前租户配置
 */
export function getAppConfig() {
    if (!appConfig) {
        console.warn('[多租户] 配置未加载，请先调用 loadAppConfig()')
    }
    return appConfig
}

/**
 * 向后兼容的 API_CONFIG 对象
 * 所有引用 API_CONFIG 的代码无需修改
 */
export const API_CONFIG = {
    get apiToken() { return appConfig?.apiToken },
    get companyId() { return appConfig?.companyId },
    get companyName() { return appConfig?.companyName },
    // 旧 baseUrl 直接指向 Java 后端，建议逐步替换为 apiBaseUrl（经 Node.js 中间件代理）
    get baseUrl() { return 'https://mvs.synwaylink.com' },
    // 业务 API 统一走 Node.js 中间件代理：本地开发走 localhost:8082，线上走当前域名
    get apiBaseUrl() { return `${getOrigin(clientConfig.apiPort)}/api/v1` },
    get approvalCode() { return appConfig?.approvalCode },
    get approvalId() { return appConfig?.approvalId },
    get returnApproverId() { return appConfig?.returnApproverId },
    get mileagePrice() { return appConfig?.mileagePrice },
    get isApprovalEnabled() { return appConfig?.isApprovalEnabled },
    // G37 硬编码高德地图 key（避免从后端动态加载时空值导致'请填写key'报错）
    get amapKey() { return 'ab7068ca37ab9cc5d2efc66efb691223' },
    get amapSecurityCode() { return 'a8fc2f3553e82bb29efb33154dbecd0d' },
    // M1: 管理后台跳转URL（支持动态配置测试/生产环境）
    get gcsPcUrl() { return appConfig?.gcsPcUrl || 'https://mvs.synwaylink.com/co/login' },
    // G16: 降级模式标识透传
    get degradedMode() { return appConfig?.degradedMode },
    get permissionHint() { return appConfig?.permissionHint },
    get tenantKey() { return appConfig?.tenantKey },
    get id() { return appConfig?.id },
    get tenantId() { return appConfig?.id },
    get isDeviceInstalled() { return appConfig?.isDeviceInstalled },
    get adminOpenId() { return appConfig?.adminOpenId },
    get adminOpenIds() { return appConfig?.adminOpenIds },
    get minApplicationMinutes() { return appConfig?.minApplicationMinutes }
}
